/**
 * The address bar's search (`?from=…`) as an external store, for `useSyncExternalStore`.
 *
 * `next/navigation`'s `useSearchParams` makes a statically rendered page (ISR, SSG) render
 * on the client only, unless wrapped in Suspense, which leaves its content out of the
 * HTML. The pages router instead gave static pages an empty `query` on the server and
 * filled it in after hydration; this store does the same: `null` on the server and while
 * hydrating, then the address bar's URL, updated on every history change.
 */

/** Fired after the app (or Next) pushes or replaces a history entry. */
const CHANGE_EVENT = "travela:historychange";
let patched = false;

/** Next moves through `history.pushState` / `replaceState`, which fire no event. */
function watchHistory(): void {
  if (patched) return;
  patched = true;
  for (const method of ["pushState", "replaceState"] as const) {
    const original = window.history[method].bind(window.history);
    window.history[method] = (...args: Parameters<History["pushState"]>) => {
      original(...args);
      // After the router's own work, never while React renders.
      queueMicrotask(() => window.dispatchEvent(new Event(CHANGE_EVENT)));
    };
  }
}

export const locationSearch = {
  subscribe(onChange: () => void): () => void {
    watchHistory();
    window.addEventListener("popstate", onChange);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener("popstate", onChange);
      window.removeEventListener(CHANGE_EVENT, onChange);
    };
  },
  /** The path and search, so any change to either is a new snapshot. */
  getSnapshot: (): string => window.location.pathname + window.location.search,
  /** Not known yet: the server, and the client while it hydrates. */
  getServerSnapshot: (): string | null => null,

  /**
   * The search (without its `?`) once the address bar shows `pathname`, else null. On a
   * client navigation Next renders the new page before it updates the address bar, so
   * for that first render the location still holds the previous page's search.
   */
  searchFor(snapshot: string | null, pathname: string): string | null {
    if (snapshot === null) return null;
    const mark = snapshot.indexOf("?");
    const path = mark === -1 ? snapshot : snapshot.slice(0, mark);
    const search = mark === -1 ? "" : snapshot.slice(mark + 1);
    const same = (a: string) => {
      try {
        return decodeURI(a);
      } catch {
        return a;
      }
    };
    return same(path) === same(pathname) ? search : null;
  },
};
